import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useScrollReveal } from '../hooks/useScrollReveal'
import { ApiError } from '../api/client'
import { authApi } from '../api/auth'
import type { IntuitExchangeResult } from '../api/auth'
import { useAuth } from '../auth/useAuth'
import { Alert } from '../components/Alert'
import { BbIcon } from '../components/BbIcon'

interface LocationState {
  from?: string
}

/**
 * Redemptions already started, keyed by code.
 *
 * The redemption is single-use and React StrictMode invokes the mount effect
 * twice, so the second pass must share the first pass's result rather than spend
 * the code again — spending it again returns 401 and overwrites a session that
 * was actually established.
 *
 * It caches the *promise*, not merely the fact that a code was seen: StrictMode
 * tears the first pass down, so that pass discards its own result and the second
 * one has to be able to adopt it. Storing the promise also means a second pass
 * arriving mid-flight waits on the real request instead of racing it.
 *
 * Module scope, not a ref — StrictMode's second pass is a remount, so a ref
 * would be a fresh one and would not see the first attempt. A real reload clears
 * this, which is the same moment the server forgets the code.
 */
const redemptions = new Map<string, Promise<IntuitExchangeResult>>()

/** The `error` codes the QuickBooks callback redirects with. */
const ERROR_MESSAGES: Record<string, string> = {
  missing_code: 'QuickBooks did not return an authorization code. Please try again.',
  invalid_state: 'That sign-in request was invalid or expired. Please try again.',
  connect_failed: 'QuickBooks authorization was declined or failed.',
  // The consent was valid but Intuit would not say who granted it — usually an
  // app whose keys do not have the OpenID scopes selected.
  no_identity:
    'QuickBooks did not share an email address, so an account could not be created. '
    + 'Sign up with your email instead, then connect the company from Settings.',
  email_unverified:
    'Your Intuit account email is not verified. Verify it with Intuit and try again.',
  no_admin_user: 'This account has no active admin to sign in as.',
  tenant_not_found: 'Account not found.',
}

/* Markup follows prototypes/batchbooks_login/code.html. The auth calls, error
   handling and post-login redirect are unchanged from the previous version of
   this page.

   QuickBooks sign-in: GET /auth/intuit-login-url returns the OAuth consent URL.
   After consent, Intuit redirects to /companies/callback with intent=login,
   which issues tokens and redirects back here with intuit_login=1 plus the
   tokens in the query. The page reads them, stores them, and navigates to /app.

   "Remember me" — tokenStore always persists to localStorage, so the checkbox
   would have nothing to toggle.
   "Forgot password?" — no password-reset endpoint exists.
   Both want a contract change before they go back in. */
export function LoginPage() {
  const authRef = useRef<HTMLDivElement>(null)
  useScrollReveal(authRef)
  const { login, acceptSession } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [reveal, setReveal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // The QuickBooks callback comes back here with either a one-time code or an
  // error reason. The code is redeemed over the API rather than carrying tokens
  // in the URL, so this effect is async and lands on a redirect only once the
  // session actually exists.
  const intuitCode = searchParams.get('intuit_code')
  const errorCode = searchParams.get('error')

  useEffect(() => {
    if (!intuitCode && !errorCode) return

    // Strip both parameters immediately: they describe a one-time event, and
    // leaving them means a reload replays it — for the code that is harmless
    // (single-use) but for an error it would nag forever.
    const clean = () => window.history.replaceState({}, '', '/login')

    if (errorCode) {
      setError(
        ERROR_MESSAGES[errorCode] ??
          'QuickBooks sign in failed. Please try again, or sign in with your email.',
      )
      clean()
      return
    }

    const code = intuitCode!
    let pending = redemptions.get(code)
    if (!pending) {
      pending = authApi.exchangeIntuitCode(code)
      redemptions.set(code, pending)

      // A failed redemption must not be cached, or a retry after a genuine
      // network blip would replay the rejection forever.
      pending.catch(() => redemptions.delete(code))
    }

    let cancelled = false
    setBusy(true)

    pending
      .then((result) => {
        if (cancelled) return null
        // Await the /me fetch before navigating: the guarded routes check
        // `user`, and navigating with it still null bounces straight back here.
        return acceptSession(result)
      })
      .then(() => {
        if (cancelled) return
        clean()
        navigate('/app', { replace: true })
      })
      .catch((cause: unknown) => {
        if (cancelled) return
        setError(
          cause instanceof ApiError
            ? cause.message
            : 'Could not complete the QuickBooks sign in. Please try again.',
        )
        setBusy(false)
        clean()
      })

    return () => {
      cancelled = true
    }
  }, [intuitCode, errorCode, acceptSession, navigate])

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await login(email, password)
      navigate((location.state as LocationState | null)?.from ?? '/app', { replace: true })
    } catch (cause: unknown) {
      setError(cause instanceof ApiError ? cause.message : 'Could not reach the server.')
    } finally {
      setBusy(false)
    }
  }

  const handleIntuitLogin = async () => {
    setBusy(true)
    setError(null)
    try {
      const { url } = await authApi.intuitLoginUrl()
      window.location.href = url
    } catch (cause: unknown) {
      setError(cause instanceof ApiError ? cause.message : 'Could not reach the server.')
      setBusy(false)
    }
  }

  return (
    <div className="bb">
      <div className="bb-auth" ref={authRef}>
        <aside className="bb-auth-aside bb-scroll-reveal">
          <div className="bb-auth-aside-in">
            <div className="bb-auth-brand">
              <BbIcon name="chart" />
              <h1>BatchBooks</h1>
            </div>
            <p className="bb-auth-tag">
              Secure, efficient, and reliable bulk data management for accounting professionals.
            </p>
            <div className="bb-auth-cards">
              <div className="bb-auth-card bb-auth-card-import bb-scroll-reveal bb-scroll-reveal-d1">
                <BbIcon name="upload" />
                <h3>Seamless Import</h3>
                <p>Handle massive datasets with zero data loss.</p>
              </div>
              <div className="bb-auth-card bb-auth-card-secure bb-scroll-reveal bb-scroll-reveal-d2">
                <BbIcon name="shield-check" />
                <h3>Bank-Grade Security</h3>
                <p>Your financial data is encrypted and secure.</p>
              </div>
            </div>
          </div>
        </aside>

        <main className="bb-auth-main">
          <div className="bb-auth-form-wrap bb-scroll-reveal bb-scroll-reveal-d1">
            <div className="bb-auth-mark">
              <BbIcon name="chart" />
              <h1>BatchBooks</h1>
            </div>

            <div className="bb-auth-head">
              <h2>Welcome back</h2>
              <p>Please enter your details to sign in.</p>
            </div>

            {error ? (
              <Alert tone="err" title="Sign in failed">
                {error}
              </Alert>
            ) : null}

            <button
              className="bb-btn bb-btn-intuit bb-btn-block"
              type="button"
              onClick={handleIntuitLogin}
              disabled={busy}
              style={{ marginBottom: 16 }}
            >
              <BbIcon name="chart" />
              Sign in with QuickBooks
            </button>

            <div className="bb-auth-divider">
              <span>or</span>
            </div>

            <form onSubmit={submit}>
              <div className="bb-field">
                <label className="bb-flabel" htmlFor="email">
                  Email address <span className="bb-req">*</span>
                </label>
                <span className="bb-input-wrap">
                  <span className="bb-input-icon">
                    <BbIcon name="mail" />
                  </span>
                  <input
                    className="bb-input bb-input-lead"
                    id="email"
                    type="email"
                    required
                    autoComplete="username"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </span>
              </div>

              <div className="bb-field">
                <label className="bb-flabel" htmlFor="password">
                  Password <span className="bb-req">*</span>
                </label>
                <span className="bb-input-wrap">
                  <span className="bb-input-icon">
                    <BbIcon name="lock" />
                  </span>
                  <input
                    className="bb-input bb-input-lead bb-input-trail"
                    id="password"
                    type={reveal ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  <button
                    className="bb-reveal"
                    type="button"
                    onClick={() => setReveal((on) => !on)}
                    aria-label={reveal ? 'Hide password' : 'Show password'}
                    aria-pressed={reveal}
                  >
                    <BbIcon name={reveal ? 'eye' : 'eye-off'} />
                  </button>
                </span>
              </div>

              <button
                className="bb-btn bb-btn-navy bb-btn-block"
                type="submit"
                disabled={busy}
                style={{ marginTop: 28 }}
              >
                {busy ? 'Signing in…' : 'Log In'}
                {busy ? null : <BbIcon name="arrow-right" />}
              </button>
            </form>

            <p className="bb-auth-alt">
              Don&rsquo;t have an account?
              <Link to="/get-started">Sign Up</Link>
            </p>
          </div>

          <div className="bb-auth-foot">
            <Link to="/get-started">Get started</Link>
            <Link to="/">Back to site</Link>
          </div>
        </main>
      </div>
    </div>
  )
}
